import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Card, Chip, CircularProgress, Typography } from '@mui/material';
import {
  ArrowForwardIos as ArrowIcon,
  CheckCircle as CheckCircleIcon,
  Image as ImageIcon,
  Inventory2 as InventoryIcon,
  Payments as PaymentsIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Sell as SellIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { useUser } from '../context/UserContext';
import { fetchConsignorDashboard } from '../api/consignorDashboard';
import type { ConsignorDashboardMetrics, RecentSaleItem } from '../api/consignorDashboard';
import type { SalePaymentStatus } from '../types/consignorSales';
import {
  CONSIGNOR_INVENTORY_PATH,
  CONSIGNOR_PROFILE_PATH,
  CONSIGNOR_SALES_PATH,
} from '../routes/routePaths';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const moneyMx = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

const fmtDate = (iso: string) =>
  new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(new Date(iso));

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AVATAR_PALETTE = [
  '#111827',
  '#1D4ED8',
  '#0F766E',
  '#7C3AED',
  '#B45309',
  '#BE185D',
  '#0369A1',
  '#15803D',
];

function avatarColor(name: string): string {
  const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
}

// ─── Status chip ──────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<SalePaymentStatus, { label: string; bg: string; color: string }> = {
  pending: { label: 'Pendiente', bg: '#FFF7ED', color: '#B45309' },
  scheduling: { label: 'Fecha solicitada', bg: '#F5F3FF', color: '#6D28D9' },
  scheduled: { label: 'Fecha asignada', bg: '#EFF6FF', color: '#1D4ED8' },
  paid: { label: 'Cobrado', bg: '#F0FDF4', color: '#15803D' },
};

// ─── Metric card ──────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string | number;
  icon: React.ReactElement;
  iconBg: string;
  iconColor: string;
  to: string;
  /** Two columns wide in the dashboard metrics grid */
  fullWidth?: boolean;
}

function MetricCard({
  label,
  value,
  icon,
  iconBg,
  iconColor,
  to,
  fullWidth,
}: MetricCardProps): React.ReactElement {
  return (
    <Card
      component={Link}
      to={to}
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid rgba(226,232,240,1)',
        borderRadius: 2.5,
        textDecoration: 'none',
        display: 'block',
        transition: 'box-shadow 0.15s',
        ...(fullWidth ? { gridColumn: '1 / -1' } : {}),
        '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          bgcolor: iconBg,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h5"
        fontWeight={800}
        sx={{ color: '#111827', lineHeight: 1.1, mb: 0.25 }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
    </Card>
  );
}

MetricCard.defaultProps = {
  fullWidth: false,
};

// ─── Quick link card ──────────────────────────────────────────────────────────

interface QuickLinkProps {
  label: string;
  sub: string;
  icon: React.ReactElement;
  to: string;
}

function QuickLink({ label, sub, icon, to }: QuickLinkProps): React.ReactElement {
  return (
    <Card
      component={Link}
      to={to}
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid rgba(226,232,240,1)',
        borderRadius: 2.5,
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 1.25,
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': {
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          borderColor: 'rgba(180,190,200,1)',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: 'rgba(248,250,252,1)',
          border: '1px solid rgba(226,232,240,1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: '#111827',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} color="text.primary">
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      </Box>
      <ArrowIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
    </Card>
  );
}

// ─── Recent sale card ─────────────────────────────────────────────────────────

function RecentSaleCard({ item }: { item: RecentSaleItem }): React.ReactElement {
  const { label, bg, color } = STATUS_CONFIG[item.paymentStatus];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        borderBottom: '1px solid rgba(226,232,240,1)',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: 'rgba(248,250,252,1)',
          border: '1px solid rgba(226,232,240,1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.imageUrl ? (
          <Box
            component="img"
            src={item.imageUrl}
            alt={item.productName}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
          />
        ) : (
          <ImageIcon sx={{ color: 'action.disabled', fontSize: 20 }} />
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {item.productName || 'Sin título'}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {item.variantName || '—'}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {fmtDate(item.soldAt)}
        </Typography>
      </Box>

      {/* Right */}
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="body2" fontWeight={800}>
          {moneyMx(item.payoutAmount)}
        </Typography>
        <Chip
          label={label}
          size="small"
          sx={{
            height: 18,
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: '0.03em',
            bgcolor: bg,
            color,
            border: '1px solid',
            borderColor: `${color}33`,
            mt: 0.5,
          }}
        />
      </Box>
    </Box>
  );
}

// ─── Pending payment banner ───────────────────────────────────────────────────

function PendingPaymentBanner({ amount }: { amount: number }): React.ReactElement {
  return (
    <Card
      component={Link}
      to={`${CONSIGNOR_SALES_PATH}?filter=scheduling`}
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        border: '1px solid rgba(251,191,36,0.45)',
        borderRadius: 2.5,
        bgcolor: '#FFFBEB',
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.07)' },
      }}
    >
      <ScheduleIcon sx={{ color: '#B45309', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} sx={{ color: '#92400E' }}>
          Pago en proceso — {moneyMx(amount)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#B45309' }}>
          El equipo asignará una fecha próximamente. Toca para ver detalles.
        </Typography>
      </Box>
      <ArrowIcon sx={{ fontSize: 13, color: '#B45309', flexShrink: 0 }} />
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function HomeDashboard(): React.ReactElement {
  const { user } = useUser();
  const name = user?.fullName ?? 'Consignador';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<ConsignorDashboardMetrics>({
    activeItems: 0,
    soldItems: 0,
    pendingPayoutAmount: 0,
    reviewItems: 0,
    hasPendingPayment: false,
    pendingPaymentAmount: 0,
    lifetimeSaleAmount: 0,
  });
  const [recentSales, setRecentSales] = useState<RecentSaleItem[]>([]);

  useEffect(() => {
    setLoading(true);
    fetchConsignorDashboard()
      .then((result) => {
        setLoading(false);
        if (!result.ok) {
          setLoadError(result.error);
          return;
        }
        setMetrics(result.data.metrics);
        setRecentSales(result.data.recentSales);
      })
      .catch(() => {
        setLoading(false);
        setLoadError('No se pudo cargar el dashboard.');
      });
  }, []);

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: 'auto',
        px: 2,
        pt: 3,
        pb: 10,
      }}
    >
      {/* Greeting */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3.5 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            bgcolor: avatarColor(name),
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 18,
            flexShrink: 0,
            userSelect: 'none',
          }}
        >
          {initials(name)}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#111827', lineHeight: 1.2 }}>
            {greeting()}, {name.split(' ')[0]}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tu resumen en Urban Utility
          </Typography>
        </Box>
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {loadError}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={28} sx={{ color: '#111827' }} />
        </Box>
      )}

      {!loading && (
        <>
          {/* Pending payment banner */}
          {metrics.hasPendingPayment && metrics.pendingPaymentAmount > 0 && (
            <PendingPaymentBanner amount={metrics.pendingPaymentAmount} />
          )}

          {/* Metric cards 2×2 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5,
              mb: 3.5,
            }}
          >
            <MetricCard
              label="En venta"
              value={metrics.activeItems}
              icon={<InventoryIcon fontSize="small" />}
              iconBg="#F0FDF4"
              iconColor="#15803D"
              to={CONSIGNOR_INVENTORY_PATH}
            />
            <MetricCard
              label="Vendidos"
              value={metrics.soldItems}
              icon={<SellIcon fontSize="small" />}
              iconBg="#F5F3FF"
              iconColor="#6D28D9"
              to={CONSIGNOR_SALES_PATH}
            />
            <MetricCard
              label="Saldo por cobrar"
              value={moneyMx(metrics.pendingPayoutAmount)}
              icon={<TrendingUpIcon fontSize="small" />}
              iconBg="#FFF7ED"
              iconColor="#B45309"
              to={`${CONSIGNOR_SALES_PATH}?filter=pending`}
            />
            <MetricCard
              label="En revisión"
              value={metrics.reviewItems}
              icon={
                metrics.reviewItems === 0 ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <PaymentsIcon fontSize="small" />
                )
              }
              iconBg={metrics.reviewItems === 0 ? '#F0FDF4' : '#EFF6FF'}
              iconColor={metrics.reviewItems === 0 ? '#15803D' : '#1D4ED8'}
              to={CONSIGNOR_INVENTORY_PATH}
            />
            <MetricCard
              label="Total vendido histórico (en tienda)"
              value={moneyMx(metrics.lifetimeSaleAmount)}
              icon={<AttachMoneyIcon fontSize="small" />}
              iconBg="#ECFDF5"
              iconColor="#047857"
              to={CONSIGNOR_SALES_PATH}
              fullWidth
            />
          </Box>

          {/* Quick links */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'text.disabled',
              mb: 1.5,
            }}
          >
            Accesos rápidos
          </Typography>
          <QuickLink
            label="Mi inventario"
            sub="Ver y gestionar tus artículos"
            icon={<InventoryIcon fontSize="small" />}
            to={CONSIGNOR_INVENTORY_PATH}
          />
          <QuickLink
            label="Mis ventas"
            sub="Historial y solicitudes de pago"
            icon={<PaymentsIcon fontSize="small" />}
            to={CONSIGNOR_SALES_PATH}
          />
          <QuickLink
            label="Mi perfil"
            sub="Nombre, correo y contraseña"
            icon={<PersonIcon fontSize="small" />}
            to={CONSIGNOR_PROFILE_PATH}
          />

          {/* Recent sales */}
          {recentSales.length > 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 3.5,
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: 'text.disabled',
                  }}
                >
                  Últimas ventas
                </Typography>
                <Typography
                  component={Link}
                  to={CONSIGNOR_SALES_PATH}
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': { color: '#111827' },
                  }}
                >
                  Ver todas →
                </Typography>
              </Box>
              <Card
                elevation={0}
                sx={{ border: '1px solid rgba(226,232,240,1)', borderRadius: 2.5, px: 2 }}
              >
                {recentSales.map((s) => (
                  <RecentSaleCard key={s.id} item={s} />
                ))}
              </Card>
            </>
          )}
        </>
      )}
    </Box>
  );
}
